import React from 'react';
import './LoadingSpinner.css';

/**
 * A professional, reusable loading spinner component
 * 
 * @param {Object} props
 * @param {string} props.size - Size of the spinner: 'sm', 'md', 'lg' (default: 'md')
 * @param {string} props.variant - Color variant: 'primary', 'secondary', 'light', 'dark' (default: 'primary')
 * @param {string} props.message - Optional loading message to display
 * @param {boolean} props.fullScreen - Whether to show as full screen overlay (default: false)
 * @param {boolean} props.inline - Whether to display inline (default: false)
 */
const LoadingSpinner = ({ 
  size = 'md', 
  variant = 'primary', 
  message = '', 
  fullScreen = false,
  inline = false 
}) => {
  const spinnerClass = `
    loading-spinner 
    loading-spinner--${size} 
    loading-spinner--${variant}
    ${inline ? 'loading-spinner--inline' : ''}
  `.trim().replace(/\s+/g, ' ');

  const borderClass = `spinner-border ${size === 'sm' ? 'spinner-border-sm' : ''} text-${variant}`.trim();

  if (fullScreen) {
    return (
      <div className="loading-spinner-overlay">
        <div className="loading-spinner-container">
          <div className={borderClass} role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          {message && <p className="loading-spinner__message">{message}</p>}
        </div>
      </div>
    );
  }

  return (
    <div className="loading-spinner-container">
      <div className={borderClass} role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
      {message && <p className="loading-spinner__message">{message}</p>}
    </div>
  );
};

/**
 * A simpler inline spinner for buttons and small spaces
 */
export const InlineSpinner = ({ size = 'sm', variant = 'light' }) => {
  return (
    <span className={`spinner-border ${size === 'sm' ? 'spinner-border-sm' : ''} text-${variant}`} role="status">
      <span className="visually-hidden">Loading...</span>
    </span>
  );
};

/**
 * Button with integrated loading state
 */
export const LoadingButton = ({ 
  children, 
  isLoading = false, 
  loadingText = 'Loading...',
  disabled = false,
  onClick,
  type = 'button',
  className = '',
  variant = 'primary',
  ...props 
}) => {
  return (
    <button
      type={type}
      className={`loading-button loading-button--${variant} ${isLoading ? 'loading-button--loading' : ''} ${className}`}
      disabled={disabled || isLoading}
      onClick={onClick}
      {...props}
    >
      {isLoading ? (
        <>
          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          <span className="loading-button__text">{loadingText}</span>
        </>
      ) : (
        children
      )}
    </button>
  );
};

export default LoadingSpinner;